import { create } from 'zustand';
import {
  type ChatEntry,
  type Phase,
  type PlayerId,
  type PrivateState,
  type RoomSnapshot,
} from '../../../shared/types';

interface GameStore {
  connected: boolean;
  error: string | null;
  roomCode: string | null;
  myPlayerId: PlayerId | null;
  identity: string | null;
  myName: string | null;
  phase: Phase;
  snapshot: RoomSnapshot | null;
  privateState: PrivateState | null;
  chat: ChatEntry[];

  setConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
  setConnectionInfo: (info: { roomCode: string; playerId: PlayerId; identity: string }) => void;
  setSnapshot: (snapshot: RoomSnapshot) => void;
  setPrivateState: (state: PrivateState) => void;
  pushChat: (entry: ChatEntry) => void;
  reset: () => void;
}

const initialSnapshot: RoomSnapshot = {
  roomCode: '',
  hostId: '',
  turnTimeoutMs: 0,
  turnDeadlineAt: null,
  phase: 'lobby',
  players: [],
  currentColor: null,
  dice: 0,
  waitingForMove: false,
  passPending: false,
  extraTurn: false,
  lastRoll: null,
  lastCapture: null,
  winnerId: null,
  chat: [],
};

const initial = {
  connected: false,
  error: null as string | null,
  roomCode: null as string | null,
  myPlayerId: null as PlayerId | null,
  identity: null as string | null,
  myName: null as string | null,
  phase: 'lobby' as Phase,
  snapshot: null as RoomSnapshot | null,
  privateState: null as PrivateState | null,
  chat: [] as ChatEntry[],
};

export const useGameStore = create<GameStore>((set) => ({
  ...initial,

  setConnected: (connected) => set({ connected }),
  setError: (error) => set({ error }),
  setConnectionInfo: ({ roomCode, playerId, identity }) =>
    set({ roomCode, myPlayerId: playerId, identity }),
  setSnapshot: (snapshot) =>
    set({
      snapshot,
      phase: snapshot.phase,
      chat: snapshot.chat,
    }),
  setPrivateState: (state) => set({ privateState: state }),
  pushChat: (entry) =>
    set((s) => {
      const chat = [...s.chat, entry];
      if (chat.length > 200) chat.shift();
      return {
        chat,
        // Keep snapshot.chat (what the UI renders) in sync immediately.
        snapshot: s.snapshot ? { ...s.snapshot, chat } : s.snapshot,
      };
    }),
  reset: () => set({ ...initial }),
}));

/** The player record for "me", or null before joining a room. */
export const useMe = () =>
  useGameStore((s) => {
    if (!s.snapshot || !s.myPlayerId) return null;
    return s.snapshot.players.find((p) => p.id === s.myPlayerId) ?? null;
  });

export const useMyColor = () => useMe()?.color ?? null;

export const useRoomCode = () => useGameStore((s) => s.roomCode);
export const usePhase = () => useGameStore((s) => s.phase);
export const useSnapshot = () => useGameStore((s) => s.snapshot);
export const usePrivateState = () => useGameStore((s) => s.privateState);